/*
 * Cart drawer upsell (e.g. gift wrap)
 *
 * Renders one Shopify line item per selected quantity so each can carry its
 * own custom message. Uses two private properties to keep separate line items
 * distinct (otherwise Shopify merges items with identical properties):
 *   _gift_wrap   -> flag we use to find/clean these line items
 *   _gift_index  -> unique 1..N index per line item
 *
 * The user-facing message is stored under a configurable property name
 * (default "Gift message") so it's rendered alongside the line item.
 */

(function () {
  'use strict';

  var SELECTORS = {
    upsell: '[data-cart-upsell]',
    toggle: '[data-cart-upsell-toggle]',
    body: '[data-cart-upsell-body]',
    qty: '[data-cart-upsell-qty]',
    increase: '[data-cart-upsell-increase]',
    decrease: '[data-cart-upsell-decrease]',
    messages: '[data-cart-upsell-messages]',
    template: '[data-cart-upsell-message-template]',
    message: '[data-cart-upsell-message]',
    status: '[data-cart-upsell-status]',
    indexLabel: '[data-cart-upsell-message-index]',
  };

  var PROP_FLAG = '_gift_wrap';
  var PROP_INDEX = '_gift_index';

  var UPDATE_DEBOUNCE = 350;
  var TYPE_DEBOUNCE = 700;

  function getCartRoute() {
    if (window.theme && window.theme.routes && window.theme.routes.cart) {
      return window.theme.routes.cart;
    }
    return '/cart';
  }

  function getCartAddRoute() {
    if (window.theme && window.theme.routes && window.theme.routes.cart_add_url) {
      return window.theme.routes.cart_add_url;
    }
    return '/cart/add';
  }

  function fetchJSON(url, options) {
    return window.fetch(url, options || {}).then(function (response) {
      if (!response.ok) {
        return response.json().then(function (json) {
          var err = new Error((json && json.message) || response.statusText);
          err.json = json;
          throw err;
        });
      }
      return response.json();
    });
  }

  function CartUpsell(el) {
    this.el = el;
    this.variantId = parseInt(el.getAttribute('data-upsell-variant-id'), 10);
    this.maxQty = parseInt(el.getAttribute('data-upsell-max-qty'), 10) || 10;
    this.messageProperty = el.getAttribute('data-upsell-message-property') || 'Gift message';

    this.toggle = el.querySelector(SELECTORS.toggle);
    this.body = el.querySelector(SELECTORS.body);
    this.qtyInput = el.querySelector(SELECTORS.qty);
    this.increaseBtn = el.querySelector(SELECTORS.increase);
    this.decreaseBtn = el.querySelector(SELECTORS.decrease);
    this.messagesContainer = el.querySelector(SELECTORS.messages);
    this.template = el.querySelector(SELECTORS.template);
    this.statusEl = el.querySelector(SELECTORS.status);

    this.busy = false;
    this.pendingTimer = null;
    this.hasPendingUpdate = false;
    this.savedMessages = [];
    this.lastAppliedSignature = null;

    if (!this.variantId || !this.toggle || !this.template) {
      return;
    }

    this.bindEvents();
    this.bindScrollLock();
    this.syncFromCart();
  }

  function getBodyScrollLock() {
    if (typeof window === 'undefined') return null;
    var vendor = window.themeVendor;
    if (!vendor) return null;
    return vendor.BodyScrollLock || vendor.bodyScrollLock || null;
  }

  CartUpsell.prototype.bindEvents = function () {
    var self = this;

    this.toggle.addEventListener('change', function () {
      self.onToggle();
    });

    this.qtyInput.addEventListener('input', function () {
      self.onQtyChange();
    });
    this.qtyInput.addEventListener('change', function () {
      self.onQtyChange();
    });

    this.increaseBtn.addEventListener('click', function (e) {
      e.preventDefault();
      self.changeQty(1);
    });
    this.decreaseBtn.addEventListener('click', function (e) {
      e.preventDefault();
      self.changeQty(-1);
    });

    this.messagesContainer.addEventListener('input', function (e) {
      if (e.target && e.target.matches(SELECTORS.message)) {
        self.scheduleUpdate(TYPE_DEBOUNCE);
      }
    });

    document.addEventListener('theme:cart:change', function (e) {
      if (self.busy || self.hasPendingUpdate) return;
      var cart = e.detail && e.detail.cart;
      self.syncFromCart(cart);
    });
  };

  /*
   * The cart drawer uses body-scroll-lock to prevent page scrolling on iOS.
   * That library blocks touch-scroll on every element except the ones it
   * explicitly tracks, which means our nested messages container can't be
   * scrolled on a real iPhone. Register the messages container with the
   * library when the cart drawer opens so iOS allows touch-scroll on it.
   * The cart drawer's clearAllBodyScrollLocks() on close will clean it up.
   */
  CartUpsell.prototype.bindScrollLock = function () {
    var self = this;
    var cartDrawer = document.querySelector('[data-drawer="drawer-cart"]');
    if (!cartDrawer) return;

    var registered = false;

    var register = function () {
      if (registered) return;
      var lib = getBodyScrollLock();
      if (lib && typeof lib.disableBodyScroll === 'function') {
        try {
          lib.disableBodyScroll(self.messagesContainer, {
            allowTouchMove: function (el) {
              return !!(el && el.closest && el.closest('[data-cart-upsell-messages]'));
            },
          });
          registered = true;
        } catch (err) {
          console.warn('CartUpsell: bodyScrollLock register failed', err);
        }
      }
    };

    var reset = function () {
      registered = false;
    };

    cartDrawer.addEventListener('theme:drawer:open', function () {
      // Wait until after the theme's own scroll:lock listener has run (it
      // schedules itself with setTimeout(1) inside showDrawer).
      window.setTimeout(register, 50);
    });
    cartDrawer.addEventListener('theme:drawer:close', reset);

    if (cartDrawer.classList.contains('drawer--visible')) {
      window.setTimeout(register, 50);
    }
  };

  CartUpsell.prototype.onToggle = function () {
    if (this.toggle.checked) {
      this.body.hidden = false;
      var current = parseInt(this.qtyInput.value, 10);
      if (!current || current < 1) {
        this.qtyInput.value = 1;
      }
      this.renderMessageFields(parseInt(this.qtyInput.value, 10));
    } else {
      this.body.hidden = true;
    }
    this.scheduleUpdate(UPDATE_DEBOUNCE);
  };

  CartUpsell.prototype.changeQty = function (delta) {
    var v = parseInt(this.qtyInput.value, 10);
    if (isNaN(v)) v = 1;
    v = Math.max(1, Math.min(this.maxQty, v + delta));
    this.qtyInput.value = v;
    this.onQtyChange();
  };

  CartUpsell.prototype.onQtyChange = function () {
    var v = parseInt(this.qtyInput.value, 10);
    if (isNaN(v) || v < 1) v = 1;
    if (v > this.maxQty) v = this.maxQty;
    if (String(v) !== this.qtyInput.value) {
      this.qtyInput.value = v;
    }
    this.renderMessageFields(v);
    this.scheduleUpdate(UPDATE_DEBOUNCE);
  };

  CartUpsell.prototype.collectCurrentMessages = function () {
    var inputs = this.messagesContainer.querySelectorAll(SELECTORS.message);
    var messages = [];
    for (var i = 0; i < inputs.length; i++) {
      messages.push(inputs[i].value);
    }
    return messages;
  };

  CartUpsell.prototype.renderMessageFields = function (qty) {
    var current = this.collectCurrentMessages();
    for (var i = 0; i < current.length; i++) {
      this.savedMessages[i] = current[i];
    }

    this.messagesContainer.innerHTML = '';
    for (var j = 0; j < qty; j++) {
      var fragment = this.template.content.cloneNode(true);
      var indexEl = fragment.querySelector(SELECTORS.indexLabel);
      if (indexEl) {
        indexEl.textContent = '#' + (j + 1);
      }
      var input = fragment.querySelector(SELECTORS.message);
      if (input) {
        input.value = this.savedMessages[j] || '';
        input.setAttribute('data-cart-upsell-message-position', String(j));
      }
      this.messagesContainer.appendChild(fragment);
    }
  };

  CartUpsell.prototype.computeSignature = function () {
    if (!this.toggle.checked) {
      return 'off';
    }
    var qty = parseInt(this.qtyInput.value, 10) || 0;
    var messages = this.collectCurrentMessages().slice(0, qty);
    return 'on:' + qty + ':' + messages.join('||');
  };

  CartUpsell.prototype.scheduleUpdate = function (delay) {
    var self = this;
    window.clearTimeout(this.pendingTimer);
    this.hasPendingUpdate = true;
    this.pendingTimer = window.setTimeout(function () {
      self.applyUpdate();
    }, delay || UPDATE_DEBOUNCE);
  };

  CartUpsell.prototype.setStatus = function (msg) {
    if (this.statusEl) {
      this.statusEl.textContent = msg || '';
    }
  };

  CartUpsell.prototype.applyUpdate = function () {
    var self = this;
    if (this.busy) {
      this.scheduleUpdate(UPDATE_DEBOUNCE);
      return;
    }

    var signature = this.computeSignature();
    if (signature === this.lastAppliedSignature) {
      this.hasPendingUpdate = false;
      return;
    }

    this.busy = true;
    this.hasPendingUpdate = false;
    this.setStatus('Updating…');

    this.fetchCart()
      .then(function (cart) {
        var existing = self.findExistingItems(cart);
        var updates = {};
        for (var i = 0; i < existing.length; i++) {
          updates[existing[i].key] = 0;
        }

        var clearPromise;
        if (Object.keys(updates).length) {
          clearPromise = fetchJSON(getCartRoute() + '/update.js', {
            method: 'post',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ updates: updates }),
          });
        } else {
          clearPromise = Promise.resolve(cart);
        }
        return clearPromise;
      })
      .then(function () {
        if (!self.toggle.checked) {
          return null;
        }
        var qty = parseInt(self.qtyInput.value, 10) || 1;
        var inputs = self.messagesContainer.querySelectorAll(SELECTORS.message);
        var items = [];
        for (var i = 0; i < qty; i++) {
          var rawMessage = inputs[i] ? inputs[i].value : '';
          var properties = {};
          properties[PROP_FLAG] = 'true';
          properties[PROP_INDEX] = String(i + 1);
          var trimmed = (rawMessage || '').trim();
          if (trimmed) {
            properties[self.messageProperty] = trimmed;
          }
          items.push({
            id: self.variantId,
            quantity: 1,
            properties: properties,
          });
        }
        if (!items.length) return null;
        return fetchJSON(getCartAddRoute() + '.js', {
          method: 'post',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: items }),
        });
      })
      .then(function () {
        return self.fetchCart();
      })
      .then(function (cart) {
        self.lastAppliedSignature = signature;
        document.dispatchEvent(
          new CustomEvent('theme:cart:change', {
            detail: { cart: cart },
            bubbles: true,
          })
        );
        document.dispatchEvent(new CustomEvent('theme:cart:reload', { bubbles: true }));
        self.setStatus('');
      })
      .catch(function (err) {
        console.error('CartUpsell update failed:', err);
        self.setStatus('Could not update gift wrap, please try again.');
      })
      .then(function () {
        self.busy = false;
      });
  };

  CartUpsell.prototype.fetchCart = function () {
    return fetchJSON(getCartRoute() + '.js');
  };

  CartUpsell.prototype.findExistingItems = function (cart) {
    if (!cart || !cart.items) return [];
    var matches = [];
    for (var i = 0; i < cart.items.length; i++) {
      var item = cart.items[i];
      if (!item || !item.properties) continue;
      var flag = item.properties[PROP_FLAG];
      if (flag === 'true' || flag === true) {
        matches.push(item);
      }
    }
    return matches;
  };

  CartUpsell.prototype.syncFromCart = function (cart) {
    var self = this;
    var promise = cart ? Promise.resolve(cart) : this.fetchCart();
    promise
      .then(function (resolved) {
        var items = self.findExistingItems(resolved);
        if (!items.length) {
          self.toggle.checked = false;
          self.body.hidden = true;
          self.lastAppliedSignature = 'off';
          return;
        }

        items.sort(function (a, b) {
          var ai = parseInt(a.properties[PROP_INDEX] || '0', 10);
          var bi = parseInt(b.properties[PROP_INDEX] || '0', 10);
          return ai - bi;
        });

        var totalQty = 0;
        var collected = [];
        for (var i = 0; i < items.length; i++) {
          totalQty += items[i].quantity;
          for (var q = 0; q < items[i].quantity; q++) {
            collected.push(items[i].properties[self.messageProperty] || '');
          }
        }

        if (totalQty > self.maxQty) totalQty = self.maxQty;
        self.savedMessages = collected.slice(0, self.maxQty);

        self.toggle.checked = true;
        self.body.hidden = false;
        self.qtyInput.value = totalQty;
        self.renderMessageFields(totalQty);
        self.lastAppliedSignature = self.computeSignature();
      })
      .catch(function (err) {
        console.error('CartUpsell sync failed:', err);
      });
  };

  function init() {
    var nodes = document.querySelectorAll(SELECTORS.upsell);
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (el.dataset.cartUpsellInited) continue;
      el.dataset.cartUpsellInited = '1';
      // eslint-disable-next-line no-new
      new CartUpsell(el);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
